import {
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
  SpcApiInspectionsInspectionIdSubmitPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";

import { Flex, NumberInput, Select, Table, TextInput } from "@mantine/core";

type TaekangInspectionLayoutType =
  | "hot-forming"
  | "laser-routine"
  | "laser-dimension";

interface Props {
  type: TaekangInspectionLayoutType;
  laserType?: string;
  criteriaList:
    | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner[]
    | undefined;
  recordForm: {
    values: SpcApiInspectionsInspectionIdSubmitPostRequest;
    setFieldValue: (path: string, value: any) => void;
  };
  detail?: boolean;
  onAnyChange?: (nextRecord: any[][]) => void;
}

const cloneRecord = (record: any[][] = []) =>
  record.map((row) => row.map((cell) => ({ ...cell })));

const computeIsPassed = (
  criteria:
    | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner
    | undefined,
  rawValue: any,
): boolean | undefined | null => {
  if (!criteria) return undefined;

  switch (criteria.type) {
    case "boolean": {
      if (
        typeof criteria.standard === "boolean" &&
        typeof rawValue === "boolean"
      ) {
        return rawValue === criteria.standard;
      }
      return undefined;
    }
    case "select": {
      const standards: any[] = Array.isArray(criteria.standard)
        ? criteria.standard
        : [];
      return standards.length ? standards.includes(rawValue) : undefined;
    }
    case "number": {
      const min = criteria.min != null ? Number(criteria.min) : undefined;
      const max = criteria.max != null ? Number(criteria.max) : undefined;
      const numericValue =
        rawValue != null && rawValue !== "" ? Number(rawValue) : undefined;
      if (numericValue == null || Number.isNaN(numericValue)) return undefined;
      if (min != null && max != null)
        return numericValue >= min && numericValue <= max;
      if (min != null) return numericValue >= min;
      if (max != null) return numericValue <= max;
      return undefined;
    }
    default:
      return undefined;
  }
};

const getCell = (recordForm: Props["recordForm"], criteriaIdx: number) =>
  recordForm.values.inspectionsInspectionIdSubmitPostRequest.record?.[
    criteriaIdx
  ]?.[0] as
    | {
        value?: string | number | null;
        isPassed?: boolean | null;
        quantity?: number | null;
      }
    | undefined;

const setSelectionValue = (
  recordForm: Props["recordForm"],
  criteria: ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
  criteriaIdx: number,
  value: string | null,
  onAnyChange?: (nextRecord: any[][]) => void,
) => {
  const base =
    recordForm.values.inspectionsInspectionIdSubmitPostRequest.record ?? [];
  const rowArr = base[criteriaIdx] ?? [];
  const prevCell = rowArr[0] ?? { value: null };
  const nextCell = {
    ...prevCell,
    value,
    isPassed: computeIsPassed(criteria, value) ?? prevCell?.isPassed,
  };

  recordForm.setFieldValue(
    `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.0`,
    nextCell,
  );
  const nextRecord = cloneRecord(base);
  nextRecord[criteriaIdx] = nextRecord[criteriaIdx] ?? [];
  nextRecord[criteriaIdx][0] = nextCell;
  onAnyChange?.(nextRecord);
};

const setQuantityValue = (
  recordForm: Props["recordForm"],
  criteriaIdx: number,
  value: number | string,
  onAnyChange?: (nextRecord: any[][]) => void,
) => {
  const parsed = value === "" ? null : (value as number | null);
  const base =
    recordForm.values.inspectionsInspectionIdSubmitPostRequest.record ?? [];
  const rowArr = base[criteriaIdx] ?? [];
  const prevCell = rowArr[0] ?? { value: null };
  const nextCell = {
    ...prevCell,
    quantity: parsed ?? undefined,
  };

  recordForm.setFieldValue(
    `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.0`,
    nextCell,
  );
  const nextRecord = cloneRecord(base);
  nextRecord[criteriaIdx] = nextRecord[criteriaIdx] ?? [];
  nextRecord[criteriaIdx][0] = nextCell;
  onAnyChange?.(nextRecord);
};

export const TaekangSpecificationInspectionExecution = ({
  type,
  laserType,
  criteriaList,
  recordForm,
  detail = false,
  onAnyChange,
}: Props) => {
  if (!criteriaList || criteriaList.length === 0) return null;

  if (type === "hot-forming") {
    return (
      <Flex>
        <Table withColumnBorders ta="center">
          <thead>
            <tr>
              <th rowSpan={3}>핫포밍</th>
              <th colSpan={8}>부적합 유형 (검사방법 : 육안검사)</th>
              <th colSpan={4}>설비에러 부적합</th>
              <th rowSpan={2}>비고</th>
            </tr>
            <tr>
              {criteriaList.slice(0, 12).map((criteria) => (
                <th key={criteria.name}>{criteria.name}</th>
              ))}
            </tr>
            <tr>
              {criteriaList.slice(0, 12).map((criteria, idx) => {
                const cell = getCell(recordForm, idx);
                const selectValue = (cell?.value as string | null) ?? null;
                const options =
                  criteria.options?.map((option) => ({
                    value: option as string,
                    label: option as string,
                  })) ?? [];

                return (
                  <th key={criteria.name}>
                    <Select
                      data={options}
                      disabled={detail}
                      value={selectValue}
                      onChange={(value) =>
                        setSelectionValue(
                          recordForm,
                          criteria,
                          idx,
                          value,
                          onAnyChange,
                        )
                      }
                      allowDeselect={false}
                      size="xs"
                    />
                  </th>
                );
              })}
            </tr>
            <tr>
              <th>수량</th>
              {Array.from({ length: 12 }).map((_, idx) => (
                <th key={idx}>
                  <NumberInput
                    key={`${recordForm.values.inspectionId}-${idx}-quantity`}
                    size="xs"
                    min={0}
                    disabled={detail}
                    value={getCell(recordForm, idx)?.quantity ?? ""}
                    onChange={(value) =>
                      setQuantityValue(recordForm, idx, value, onAnyChange)
                    }
                  />
                </th>
              ))}
              <th rowSpan={2}>
                <TextInput
                  value={
                    recordForm.values.inspectionsInspectionIdSubmitPostRequest
                      .description
                  }
                  disabled={detail}
                  onChange={(event) =>
                    recordForm.setFieldValue(
                      "inspectionsInspectionIdSubmitPostRequest.description",
                      event.currentTarget.value,
                    )
                  }
                />
              </th>
            </tr>
          </thead>
        </Table>
      </Flex>
    );
  }

  if (type === "laser-routine") {
    return (
      <Flex>
        <Table withColumnBorders ta="center">
          <thead>
            <tr>
              <th rowSpan={3}>
                레이저{" "}
                {laserType === "laser_routine" ? "(자주검사)" : "(순회검사)"}
              </th>
              <th colSpan={12}>부적합 유형 (검사방법 : 육안검사)</th>
              <th colSpan={3}>설비에러 부적합</th>
              <th rowSpan={3}>비고</th>
            </tr>
            <tr>
              {criteriaList.slice(0, 15).map((criteria) => (
                <th key={criteria.name}>{criteria.name}</th>
              ))}
            </tr>
            <tr>
              {criteriaList.slice(0, 15).map((criteria, idx) => {
                const cell = getCell(recordForm, idx);
                const selectValue = (cell?.value as string | null) ?? null;
                const options =
                  criteria.options?.map((option) => ({
                    value: option as string,
                    label: option as string,
                  })) ?? [];

                return (
                  <th key={criteria.name}>
                    <Select
                      data={options}
                      disabled={detail}
                      value={selectValue}
                      onChange={(value) =>
                        setSelectionValue(
                          recordForm,
                          criteria,
                          idx,
                          value,
                          onAnyChange,
                        )
                      }
                      allowDeselect={false}
                      size="xs"
                    />
                  </th>
                );
              })}
              <th rowSpan={2} />
            </tr>
            <tr>
              <th>수량</th>
              {Array.from({ length: 15 }).map((_, idx) => (
                <th key={idx}>
                  <NumberInput
                    key={`${recordForm.values.inspectionId}-${idx}-quantity`}
                    size="xs"
                    min={0}
                    disabled={detail}
                    value={getCell(recordForm, idx)?.quantity ?? ""}
                    onChange={(value) =>
                      setQuantityValue(recordForm, idx, value, onAnyChange)
                    }
                  />
                </th>
              ))}
              <th rowSpan={2}>
                <TextInput
                  value={
                    recordForm.values.inspectionsInspectionIdSubmitPostRequest
                      .description
                  }
                  disabled={detail}
                  onChange={(event) =>
                    recordForm.setFieldValue(
                      "inspectionsInspectionIdSubmitPostRequest.description",
                      event.currentTarget.value,
                    )
                  }
                />
              </th>
            </tr>
          </thead>
        </Table>
      </Flex>
    );
  }

  return (
    <Flex>
      <Table withColumnBorders ta="center">
        <thead>
          <tr>
            <th rowSpan={2}>레이저(치수)</th>
            <th colSpan={12}>{criteriaList[0]?.name}</th>
          </tr>
          <tr>
            {criteriaList.slice(0, 1).map((criteria, idx) => {
              const cell = getCell(recordForm, idx);
              const selectValue = (cell?.value as string | null) ?? null;
              const options =
                criteria.options?.map((option) => ({
                  value: option as string,
                  label: option as string,
                })) ?? [];

              return (
                <th key={criteria.name}>
                  <Select
                    data={options}
                    disabled={detail}
                    value={selectValue}
                    onChange={(value) =>
                      setSelectionValue(
                        recordForm,
                        criteria,
                        idx,
                        value,
                        onAnyChange,
                      )
                    }
                    allowDeselect={false}
                    size="xs"
                  />
                </th>
              );
            })}
          </tr>
          <tr>
            <th>수량</th>
            {Array.from({ length: 1 }).map((_, idx) => (
              <th key={idx}>
                <NumberInput
                  key={`${recordForm.values.inspectionId}-${idx}-quantity`}
                  size="xs"
                  min={0}
                  disabled={detail}
                  value={getCell(recordForm, idx)?.quantity ?? ""}
                  onChange={(value) =>
                    setQuantityValue(recordForm, idx, value, onAnyChange)
                  }
                />
              </th>
            ))}
          </tr>
        </thead>
      </Table>
    </Flex>
  );
};
